import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaGripHorizontal, FaTimes, FaBackspace } from "react-icons/fa";
import styles from "./TamilKeyBoard.module.css";

const TAMIL_VOWELS = [
  "அ",
  "ஆ",
  "இ",
  "ஈ",
  "உ",
  "ஊ",
  "எ",
  "ஏ",
  "ஐ",
  "ஒ",
  "ஓ",
  "ஔ",
];

const TAMIL_CONSONANTS = [
  "க",
  "ங",
  "ச",
  "ஞ",
  "ட",
  "ண",
  "த",
  "ந",
  "ப",
  "ம",
  "ய",
  "ர",
  "ல",
  "வ",
  "ழ",
  "ள",
  "ற",
  "ன",
  "ஜ",
  "ஷ",
  "ஸ",
  "ஹ",
];

const TAMIL_MODIFIERS = [
  "ா",
  "ி",
  "ீ",
  "ு",
  "ூ",
  "ெ",
  "ே",
  "ை",
  "ொ",
  "ோ",
  "ௌ",
  "்",
];

const TamilKeyBoard = ({
  show,
  onClose,
  onKeyPress,
  onBackspace,
  onSpace,
  title = "தமிழ் விசைப்பலகை",
  initialPosition = { x: 100, y: 120 },
}) => {
  const keyboardRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const lastFocusedInput = useRef(null);

  const [kbPosition, setKbPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);

  // ---- Track last focused input/textarea so keyboard knows where to type ----
  useEffect(() => {
    const handleFocus = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") {
        lastFocusedInput.current = e.target;
      }
    };
    document.addEventListener("focusin", handleFocus);
    return () => document.removeEventListener("focusin", handleFocus);
  }, []);

  // ---- Helpers to insert / delete in the focused input (React-safe) ----
  const updateInput = useCallback((newValue, newCaretPos) => {
    const el = lastFocusedInput.current;
    if (!el) return;
    const proto =
      el.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
    setter.call(el, newValue);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    requestAnimationFrame(() => {
      el.focus();
      try {
        el.setSelectionRange(newCaretPos, newCaretPos);
      } catch (_) {
        /* some input types don't support selection */
      }
    });
  }, []);

  const insertIntoFocusedInput = useCallback(
    (char) => {
      const el = lastFocusedInput.current;
      if (!el) return;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const newValue =
        el.value.substring(0, start) + char + el.value.substring(end);
      updateInput(newValue, start + char.length);
    },
    [updateInput],
  );

  const defaultBackspace = useCallback(() => {
    const el = lastFocusedInput.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    if (start === end) {
      if (start === 0) return;
      const newValue =
        el.value.substring(0, start - 1) + el.value.substring(end);
      updateInput(newValue, start - 1);
    } else {
      const newValue = el.value.substring(0, start) + el.value.substring(end);
      updateInput(newValue, start);
    }
  }, [updateInput]);

  // ---- Event handlers (use props if provided, else fall back to focused input) ----
  const handleTamilKeyPress = (char) => {
    if (onKeyPress) onKeyPress(char);
    else insertIntoFocusedInput(char);
  };

  const handleTamilBackspace = () => {
    if (onBackspace) onBackspace();
    else defaultBackspace();
  };

  const handleTamilSpace = () => {
    if (onSpace) onSpace();
    else insertIntoFocusedInput(" ");
  };

  // ---- Drag logic ----
  const handleDragStart = (e) => {
    const point = e.touches ? e.touches[0] : e;
    const rect = keyboardRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const point = e.touches ? e.touches[0] : e;
      const kb = keyboardRef.current;
      if (!kb) return;
      const newX = point.clientX - dragOffset.current.x;
      const newY = point.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - kb.offsetWidth;
      const maxY = window.innerHeight - kb.offsetHeight;
      setKbPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleEnd = () => setIsDragging(false);

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleEnd);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging]);

  if (!show) return null;

  return (
    <div
      ref={keyboardRef}
      className={styles.keyboard}
      style={{ top: `${kbPosition.y}px`, left: `${kbPosition.x}px` }}
    >
      <div
        className={`${styles.header} ${isDragging ? styles.dragging : ""}`}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className={styles.headerLeft}>
          <FaGripHorizontal size={14} className={styles.gripIcon} />
          <h5 className={styles.title}>{title}</h5>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={styles.closeBtn}
          aria-label="Close keyboard"
        >
          <FaTimes />
        </button>
      </div>

      <div className={styles.content}>
        {/* Vowels */}
        <div className={styles.keyRow}>
          {TAMIL_VOWELS.map((char) => (
            <button
              key={char}
              type="button"
              onClick={() => handleTamilKeyPress(char)}
              className={styles.key}
            >
              {char}
            </button>
          ))}
        </div>

        {/* Consonants */}
        <div className={styles.keyRow}>
          {TAMIL_CONSONANTS.map((char) => (
            <button
              key={char}
              type="button"
              onClick={() => handleTamilKeyPress(char)}
              className={styles.key}
            >
              {char}
            </button>
          ))}
        </div>

        {/* Modifiers + Backspace + Space */}
        <div className={styles.keyRow}>
          {TAMIL_MODIFIERS.map((char, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleTamilKeyPress(char)}
              className={styles.key}
            >
              {char}
            </button>
          ))}
          <button
            type="button"
            onClick={handleTamilBackspace}
            className={`${styles.key} ${styles.backspaceKey}`}
            aria-label="Backspace"
          >
            <FaBackspace size={18} />
          </button>
          <button
            type="button"
            onClick={handleTamilSpace}
            className={`${styles.key} ${styles.spaceKey}`}
            aria-label="Space"
          >
            Space
          </button>
        </div>
      </div>
    </div>
  );
};

export default TamilKeyBoard;
